import type { CreditLayerUsage } from '@nadoutong/contracts';
import { DualCurrencyAmount } from '../DualCurrencyAmount.tsx';
import { usdMicrosToCnyCents } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import { cx } from '../../ui/cx.ts';

/**
 * Stacked prepaid / long-term / temporary usage bar (router#2010).
 *
 * Credit floors use used/remaining. Prepaid is remaining only — it is
 * not a pool. Over-limit is a fourth segment. Colors are tokens only.
 */

export interface CreditLayerUsageBarProps {
  readonly layers: CreditLayerUsage;
  readonly cnyPerUsd: number | null;
  /**
   * Locked-contract CNY for the long-term / temporary caps when the
   * 360 overview has them. Used so the bar caption cannot diverge from
   * the KPI that already prefers `credit_limit_cny_cents`.
   */
  readonly lockedCapCnyCents?: {
    readonly long_term?: number | null;
    readonly temporary?: number | null;
  };
  readonly testId?: string;
}

interface VisibleLayer {
  readonly id: 'prepaid' | 'long_term' | 'temporary' | 'over_limit';
  readonly width: number;
  readonly used: number;
  readonly fill: string;
  readonly dim: string;
  readonly border: string;
  readonly labelKey:
    | 'credit.overview.layer.prepaid'
    | 'credit.overview.layer.long_term'
    | 'credit.overview.layer.temporary'
    | 'credit.overview.layer.over_limit';
  readonly labelMode: 'remaining' | 'used_of_cap' | 'over';
}

function visibleLayers(layers: CreditLayerUsage): readonly VisibleLayer[] {
  const items: VisibleLayer[] = [];
  if (layers.prepaid_remaining_usd_micros > 0) {
    items.push({
      id: 'prepaid',
      width: layers.prepaid_remaining_usd_micros,
      used: layers.prepaid_remaining_usd_micros,
      fill: 'bg-ok',
      dim: 'bg-ok-dim',
      border: 'border-ok-line',
      labelKey: 'credit.overview.layer.prepaid',
      labelMode: 'remaining',
    });
  }
  if (layers.long_term_cap_usd_micros > 0) {
    items.push({
      id: 'long_term',
      width: layers.long_term_cap_usd_micros,
      used: layers.long_term_used_usd_micros,
      fill: 'bg-signal',
      dim: 'bg-signal-dim',
      border: 'border-signal-deep/50',
      labelKey: 'credit.overview.layer.long_term',
      labelMode: 'used_of_cap',
    });
  }
  if (layers.temporary_cap_usd_micros > 0) {
    items.push({
      id: 'temporary',
      width: layers.temporary_cap_usd_micros,
      used: layers.temporary_used_usd_micros,
      fill: 'bg-warn',
      dim: 'bg-warn-dim',
      border: 'border-warn-line',
      labelKey: 'credit.overview.layer.temporary',
      labelMode: 'used_of_cap',
    });
  }
  if (layers.over_limit_usd_micros > 0) {
    items.push({
      id: 'over_limit',
      width: layers.over_limit_usd_micros,
      used: layers.over_limit_usd_micros,
      fill: 'bg-bad',
      dim: 'bg-bad-dim',
      border: 'border-bad-line',
      labelKey: 'credit.overview.layer.over_limit',
      labelMode: 'over',
    });
  }
  return items;
}

function LayerMoney({ cnyCents }: { readonly cnyCents: number | null }): JSX.Element {
  return <DualCurrencyAmount display="cny" cnyCents={cnyCents} signed={false} align="start" />;
}

function lockedCapFor(
  id: VisibleLayer['id'],
  locked: CreditLayerUsageBarProps['lockedCapCnyCents'],
): number | null | undefined {
  if (id === 'long_term') return locked?.long_term;
  if (id === 'temporary') return locked?.temporary;
  return null;
}

function layerCnyCents(
  usdMicros: number,
  capUsdMicros: number,
  lockedCapCnyCents: number | null | undefined,
  cnyPerUsd: number | null,
): number | null {
  if (
    typeof lockedCapCnyCents === 'number' &&
    Number.isFinite(lockedCapCnyCents) &&
    capUsdMicros !== 0
  ) {
    return Math.round((usdMicros * lockedCapCnyCents) / capUsdMicros);
  }
  return usdMicrosToCnyCents(usdMicros, cnyPerUsd);
}

export function CreditLayerUsageBar({
  layers,
  cnyPerUsd,
  lockedCapCnyCents,
  testId = 'credit-layer-bar',
}: CreditLayerUsageBarProps): JSX.Element {
  const t = useT();
  const segments = visibleLayers(layers);
  let totalWidth = 0;
  let totalUsed = 0;
  for (const segment of segments) {
    totalWidth += segment.width;
    totalUsed += segment.used;
  }
  const caretPct = totalWidth > 0 ? (totalUsed / totalWidth) * 100 : 0;

  return (
    <div data-testid={testId} className="flex flex-col gap-2">
      <div className="flex h-6 gap-0.5" data-testid={`${testId}-track`}>
        {segments.length === 0 ? (
          <div className="h-6 w-full rounded bg-well" data-testid={`${testId}-empty`} />
        ) : (
          segments.map((segment, index) => {
            const usedPct = segment.width > 0 ? (segment.used / segment.width) * 100 : 0;
            const first = index === 0;
            const last = index === segments.length - 1;
            return (
              <div
                key={segment.id}
                data-testid={`${testId}-segment-${segment.id}`}
                data-used={String(segment.used)}
                data-width={String(segment.width)}
                className={cx(
                  'relative overflow-hidden border',
                  segment.dim,
                  segment.border,
                  first && last && 'rounded',
                  first && !last && 'rounded-l',
                  last && !first && 'rounded-r',
                )}
                style={{ flex: `${segment.width} 1 0` }}
              >
                <div
                  className={cx('absolute inset-y-0 left-0', segment.fill)}
                  style={{ width: `${usedPct}%` }}
                  data-testid={`${testId}-used-${segment.id}`}
                />
              </div>
            );
          })
        )}
      </div>
      {totalWidth > 0 ? (
        <div className="relative h-2">
          <div
            className="absolute top-0 h-2 w-0.5 bg-paper"
            style={{ left: `${caretPct}%`, transform: 'translateX(-50%)' }}
            data-testid={`${testId}-caret`}
          />
        </div>
      ) : null}
      <div className="flex gap-0.5">
        {segments.map((segment) => (
          <div
            key={segment.id}
            className="min-w-0"
            style={{ flex: `${segment.width} 1 0` }}
            data-testid={`${testId}-label-${segment.id}`}
          >
            <div className="flex items-center gap-1.5 text-dense font-medium text-paper">
              <span className={cx('h-2 w-2 shrink-0 rounded-sm', segment.fill)} />
              <span className="truncate">{t(segment.labelKey)}</span>
            </div>
            <div className="font-mono text-label text-paper-mid">
              {segment.labelMode === 'used_of_cap' ? (
                <div className="flex items-start gap-1">
                  <LayerMoney
                    cnyCents={layerCnyCents(
                      segment.used,
                      segment.width,
                      lockedCapFor(segment.id, lockedCapCnyCents),
                      cnyPerUsd,
                    )}
                  />
                  <span className="text-paper-low">/</span>
                  <LayerMoney
                    cnyCents={layerCnyCents(
                      segment.width,
                      segment.width,
                      lockedCapFor(segment.id, lockedCapCnyCents),
                      cnyPerUsd,
                    )}
                  />
                </div>
              ) : (
                <LayerMoney
                  cnyCents={layerCnyCents(
                    segment.width,
                    segment.width,
                    lockedCapFor(segment.id, lockedCapCnyCents),
                    cnyPerUsd,
                  )}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
